import React from "react";
import {
    Button,
    createStyles,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Theme,
    withStyles,
    WithStyles
} from "@material-ui/core";
import {
    ArrowLeft,
    ArrowRight,
    Delete,
    MoreHoriz,
    SkipNext,
    SkipPrevious
} from "@material-ui/icons";
import ImageGallery, { ReactImageGalleryItem } from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import Constants from "../../models/utility/Constants";

type ImageCarouselProps = WithStyles<typeof styles> & {
    backButtonText?: string;
    deleteFn?: (name: string) => (e: React.MouseEvent) => void;
    reorderFn?: (
        name: string,
        newIndex: number
    ) => (e: React.MouseEvent) => void;
    fixedRatio?: boolean;
    images: ReactImageGalleryItem[];
    maxWidth?: number;
    showThumbnailControls?: boolean;
    showThumbnails?: boolean;
};

interface IImageCarouselState {
    fullScreen: boolean;
    maxHeight: number;
    thumbMenuEl: HTMLElement | null;
    thumbMenuImageId: string | null;
}

const maxWidth = 600;
const ratio = 1.5;
const styles = (theme: Theme) =>
    createStyles({
        backButtonDiv: {
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            left: 0,
            padding: theme.spacing(4),
            position: "fixed",
            right: 0,
            width: "100%",
            zIndex: 1500
        },
        carousel: {
            "& .image-gallery-fullscreen-button:hover:before": {
                color: theme.palette.primary.main
            },
            "& .image-gallery-left-nav:hover:before": {
                color: theme.palette.primary.main
            },
            "& .image-gallery-play-button:hover:before": {
                color: theme.palette.primary.main
            },
            "& .image-gallery-right-nav:hover:before": {
                color: theme.palette.primary.main
            },
            "& .image-gallery-slide": {
                backgroundColor: "transparent"
            },
            "& .image-gallery-slide img": {
                maxHeight: maxWidth / ratio,
                width: Constants.IsIE ? "100% !important" : "auto !important"
            },
            "& .image-gallery-slide-wrapper": {
                backgroundColor: theme.palette.background.overlay
            },
            "& .image-gallery-thumbnail.active": {
                border: `4px solid ${theme.palette.primary.main}`
            },
            "& .image-gallery.fullscreen-modal": {
                background: theme.palette.background.overlay,
                zIndex: theme.zIndex.tooltip
            },
            flexGrow: 1,
            maxWidth
        },
        thumbDelete: {
            padding: theme.spacing(0.5),
            position: "absolute",
            right: 0,
            top: 0
        },
        thumbDeleteBtn: {
            color: theme.palette.common.white,
            padding: 0
        },
        thumbMoreIcon: {
            filter: "drop-shadow(2px 2px 2px black)",
            msFilter: "drop-shadow(2px 2px 2px black)",
            webkitFilter: "drop-shadow(2px 2px 2px black)"
        },
        deleteMenuItem: {
            color: theme.palette.error.main
        },
        arrowMenuItemIcon: {
            marginRight: 0
        },
        arrowMenuItemIconSvg: {
            marginLeft: -8,
            marginRight: 12
        }
    });

class ImageCarousel extends React.PureComponent<
    ImageCarouselProps,
    IImageCarouselState
> {
    private imageGalleryRef: React.RefObject<ImageGallery>;
    private containerRef: React.RefObject<HTMLDivElement>;

    constructor(props: ImageCarouselProps) {
        super(props);

        this.imageGalleryRef = React.createRef();
        this.containerRef = React.createRef();
        this.state = {
            fullScreen: false,
            maxHeight: maxWidth / ratio,
            thumbMenuEl: null,
            thumbMenuImageId: null
        };
    }

    public componentDidMount() {
        if (this.props.fixedRatio) {
            window.addEventListener(
                "deviceorientation",
                this.resizeImageDiv.bind(this)
            );
            window.addEventListener(
                "orientationchange",
                this.resizeImageDiv.bind(this)
            );
            window.addEventListener("resize", this.resizeImageDiv.bind(this));

            this.resizeImageDiv();
        }
    }

    public componentWillUnmount() {
        if (this.props.fixedRatio) {
            window.removeEventListener(
                "deviceorientation",
                this.resizeImageDiv
            );
            window.removeEventListener(
                "orientationchange",
                this.resizeImageDiv
            );
            window.removeEventListener("resize", this.resizeImageDiv);
        }
    }

    public render() {
        const classes = this.props.classes;
        const propStyle = {
            maxWidth: this.props.maxWidth || maxWidth
        } as React.CSSProperties;

        const showThumbnails =
            this.props.showThumbnails !== undefined ||
            this.props.showThumbnails !== null
                ? this.props.showThumbnails
                : true;

        let images = this.props.images;
        images = [
            ...this.props.images.map((e, i, a) => {
                return {
                    ...e,
                    renderItem: this.renderImage,
                    renderThumbInner: this.props.showThumbnailControls
                        ? this.renderThumbWithMenu(i, a)
                        : undefined
                };
            })
        ];

        return (
            <div
                ref={this.containerRef}
                className={`${classes.carousel} ${
                    showThumbnails || this.state.fullScreen
                        ? ""
                        : "hideThumbnails"
                }`}
                style={propStyle}
            >
                <ImageGallery
                    ref={this.imageGalleryRef}
                    items={images}
                    showPlayButton={false}
                    useBrowserFullscreen={false}
                    showBullets={true}
                    showThumbnails={true}
                    onScreenChange={this.onFullScreen}
                    lazyLoad={true}
                />

                {this.state.fullScreen && this.renderBackButton()}
            </div>
        );
    }

    private renderBackButton = () => {
        const classes = this.props.classes;

        return (
            <div className={classes.backButtonDiv}>
                <Button
                    size="large"
                    variant="outlined"
                    color="primary"
                    onClick={this.imageGalleryRef.current?.exitFullScreen.bind(
                        this.imageGalleryRef.current
                    )}
                >
                    {this.props.backButtonText || "Go Back"}
                </Button>
            </div>
        );
    };

    private resizeImageDiv = () => {
        const container = this.containerRef.current;
        if (container) {
            // find affected div
            const galleryDiv = container.getElementsByClassName(
                "image-gallery-slide-wrapper"
            )[0];

            // maintain aspect ratio
            const width = galleryDiv.clientWidth;

            const maxHeight = (this.props.maxWidth || maxWidth) / ratio;
            const newHeight = Math.min(maxHeight, width / ratio);

            galleryDiv.setAttribute("style", `height: ${newHeight}px;`);

            if (this.props.fixedRatio) {
                this.setState({ maxHeight: newHeight });
            }
        }
    };

    private renderImage = (item: ReactImageGalleryItem) => {
        let style = {};
        if (this.props.fixedRatio) {
            style = { maxHeight: this.state.maxHeight };
        }

        return (
            <img
                src={item.original}
                alt={item.originalAlt}
                title={item.originalTitle}
                style={style}
            />
        );
    };

    handleThumbClick =
        (imageId: string) =>
        (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
            this.setState({
                thumbMenuEl: event.currentTarget,
                thumbMenuImageId: imageId
            });
        };

    handleThumbClose = () => {
        this.setState({ thumbMenuEl: null, thumbMenuImageId: null });
    };

    handleThumbDelete =
        (item: ReactImageGalleryItem) =>
        (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
            if (this.props.deleteFn) {
                this.props.deleteFn(item.originalAlt || "")(event);
            }
            this.handleThumbClose();
        };

    handleThumbReorder =
        (item: ReactImageGalleryItem, newIndex: number) =>
        (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
            if (this.props.reorderFn) {
                this.props.reorderFn(item.originalAlt || "", newIndex)(event);
            }
            this.handleThumbClose();
        };

    private renderThumbWithMenu =
        (index: number, array: ReactImageGalleryItem[]) =>
        (item: ReactImageGalleryItem) => {
            const classes = this.props.classes;

            return (
                <div className="image-gallery-thumbnail-inner">
                    {!!this.props.deleteFn && item.originalAlt && (
                        <div className={classes.thumbDelete}>
                            <IconButton
                                className={classes.thumbDeleteBtn}
                                onClick={this.handleThumbClick(
                                    item.originalAlt
                                )}
                            >
                                <MoreHoriz
                                    className={classes.thumbMoreIcon}
                                    color="inherit"
                                />
                            </IconButton>
                            <Menu
                                id={"image-gallery-thumb-menu-" + index}
                                anchorEl={this.state.thumbMenuEl}
                                open={
                                    this.state.thumbMenuImageId ===
                                    item.originalAlt
                                }
                                onClose={this.handleThumbClose}
                            >
                                {index > 0 && (
                                    <MenuItem
                                        onClick={this.handleThumbReorder(
                                            item,
                                            index - 1
                                        )}
                                    >
                                        <ListItemIcon
                                            className={
                                                classes.arrowMenuItemIcon
                                            }
                                        >
                                            <ArrowLeft
                                                className={
                                                    classes.arrowMenuItemIconSvg
                                                }
                                                fontSize="large"
                                            />
                                        </ListItemIcon>
                                        <ListItemText>Move Left</ListItemText>
                                    </MenuItem>
                                )}
                                {index > 0 && (
                                    <MenuItem
                                        onClick={this.handleThumbReorder(
                                            item,
                                            0
                                        )}
                                    >
                                        <ListItemIcon>
                                            <SkipPrevious />
                                        </ListItemIcon>
                                        <ListItemText>
                                            Move to First
                                        </ListItemText>
                                    </MenuItem>
                                )}
                                {index < array.length - 1 && (
                                    <MenuItem
                                        onClick={this.handleThumbReorder(
                                            item,
                                            index + 1
                                        )}
                                    >
                                        <ListItemIcon
                                            className={
                                                classes.arrowMenuItemIcon
                                            }
                                        >
                                            <ArrowRight
                                                className={
                                                    classes.arrowMenuItemIconSvg
                                                }
                                                fontSize="large"
                                            />
                                        </ListItemIcon>
                                        <ListItemText>Move Right</ListItemText>
                                    </MenuItem>
                                )}
                                {index < array.length - 1 && (
                                    <MenuItem
                                        onClick={this.handleThumbReorder(
                                            item,
                                            array.length - 1
                                        )}
                                    >
                                        <ListItemIcon>
                                            <SkipNext />
                                        </ListItemIcon>
                                        <ListItemText>
                                            Move to Last
                                        </ListItemText>
                                    </MenuItem>
                                )}
                                <MenuItem
                                    onClick={this.handleThumbDelete(item)}
                                >
                                    <ListItemIcon
                                        className={classes.deleteMenuItem}
                                    >
                                        <Delete />
                                    </ListItemIcon>
                                    <ListItemText
                                        primaryTypographyProps={{
                                            className: classes.deleteMenuItem
                                        }}
                                    >
                                        Delete Image
                                    </ListItemText>
                                </MenuItem>
                            </Menu>
                        </div>
                    )}
                    <img
                        src={item.thumbnail}
                        alt={item.thumbnailAlt}
                        title={item.thumbnailTitle}
                    />
                    {item.thumbnailLabel && (
                        <div className="image-gallery-thumbnail-label">
                            {item.thumbnailLabel}
                        </div>
                    )}
                </div>
            );
        };

    private onFullScreen = (e: Element) => this.setState({ fullScreen: !!e });
}

export default withStyles(styles)(ImageCarousel);
